import { t } from "@travily/i18n";
import { ActionMenu } from "@ui/components/ActionMenu";
import { Icon } from "@ui/components/Icon";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button, type BaseButtonProps as RACButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";

const friendshipButton = tv({
  base: "inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full font-semibold",
  defaultVariants: {
    size: "40",
    status: "default",
  },
  variants: {
    size: {
      "36": "gap-1.5 px-3 py-1 text-base",
      "40": "gap-2 px-4 py-[7px] text-lg leading-6",
    },
    status: {
      approved: [
        "border-2 border-transparent bg-pink-50 text-pink-600/80",
        "hover:border-pink-700 hover:bg-pink-25 hover:text-pink-600",
        "pressed:border-pink-700 pressed:bg-pink-50 pressed:text-pink-700",
      ],
      default: [
        "border-2 border-neutral-100 bg-white text-neutral-600",
        "hover:border-yellow-600 hover:bg-yellow-25 hover:text-yellow-700",
        "pressed:border-yellow-600 pressed:bg-yellow-50 pressed:text-yellow-700",
      ],
      invited: "border-2 border-transparent bg-yellow-100 text-yellow-700",
      received: [
        "border-2 border-transparent bg-yellow-100 text-yellow-700",
        "hover:border-yellow-600 hover:bg-yellow-25 hover:text-yellow-700",
        "pressed:border-yellow-600 pressed:bg-yellow-50 pressed:text-yellow-700",
      ],
    },
  },
});

type FriendshipButtonVariants = VariantProps<typeof friendshipButton>;

export type FriendshipStatus = NonNullable<FriendshipButtonVariants["status"]>;

/** Maps backend friendship_status values to FriendshipButton status variants. */
export const BACKEND_STATUS_MAP: Record<string, FriendshipStatus> = {
  none: "default",
  pending_sent: "invited",
  pending_received: "received",
  accepted: "approved",
};

// Action-only: friendship requests trigger an API call, never a navigation.
// Pinned to RACButtonProps directly (not the polymorphic ButtonProps union) so callers
// can't accidentally pass `href` and turn this into a link.
export type FriendshipButtonProps = Omit<RACButtonProps, "children"> & {
  menuPlacement?: "bottom" | "bottom start" | "bottom end";
  onAcceptFriendship?: () => void;
  onDeclineFriendship?: () => void;
  onUnfriend?: () => void;
  size?: FriendshipButtonVariants["size"];
  status?: FriendshipStatus;
};

// i18n-tasks-use t('components.views.shared.friendship_button.accept')
// i18n-tasks-use t('components.views.shared.friendship_button.approved')
// i18n-tasks-use t('components.views.shared.friendship_button.decline')
// i18n-tasks-use t('components.views.shared.friendship_button.default')
// i18n-tasks-use t('components.views.shared.friendship_button.invited')
// i18n-tasks-use t('components.views.shared.friendship_button.received')
const LABEL_KEYS: Record<FriendshipStatus, string> = {
  approved: "components.views.shared.friendship_button.approved",
  default: "components.views.shared.friendship_button.default",
  invited: "components.views.shared.friendship_button.invited",
  received: "components.views.shared.friendship_button.received",
};

const ICONS: Record<FriendshipStatus, { color?: "pink" | "yellow"; name: "heart2" | "heart"; type?: "flat" }> = {
  approved: { color: "pink", name: "heart", type: "flat" },
  default: { name: "heart2" },
  invited: { color: "yellow", name: "heart", type: "flat" },
  received: { color: "yellow", name: "heart", type: "flat" },
};

export function FriendshipButton({
  size = "40",
  status = "default",
  menuPlacement = "bottom",
  onAcceptFriendship,
  onDeclineFriendship,
  onUnfriend,
  ...props
}: FriendshipButtonProps) {
  const iconSize = size === "40" ? "20" : "16";
  const icon = ICONS[status];

  const buttonElement = (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        friendshipButton({ ...renderProps, size, status, className }),
      )}
    >
      <Icon color={icon.color} name={icon.name} size={iconSize} type={icon.type} />
      {t(LABEL_KEYS[status])}
    </Button>
  );

  const menuItems =
    status === "received"
      ? [
          {
            id: "accept",
            label: t("components.views.shared.friendship_button.accept"),
            icon: <Icon name="userCheck" />,
            onAction: onAcceptFriendship,
          },
          {
            id: "decline",
            label: t("components.views.shared.friendship_button.decline"),
            icon: <Icon name="userX" />,
            onAction: onDeclineFriendship,
            destructive: true,
          },
        ]
      : status === "approved"
        ? [
            {
              id: "unfriend",
              label: t("common.profile.unfriend"),
              icon: <Icon name="userX" />,
              onAction: onUnfriend,
              destructive: true,
            },
          ]
        : [];

  if (menuItems.length > 0) {
    return <ActionMenu items={menuItems} placement={menuPlacement} trigger={buttonElement} />;
  }

  return buttonElement;
}
